import React, { useEffect, useState } from "react";
import {
  adminListLeagues, adminDissolveLeague, adminTransferLeagueOwnership,
  adminPauseLeague, cloneLeague, exportLeague,
} from "../../lib/api";

/**
 * LeaguesSection — admin search + moderation for leagues. Supports
 * ownership transfer and full-cascade dissolve.
 */
export function LeaguesSection({ onToast, setAuthed }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const reload = async () => {
    try { setRows(await adminListLeagues(q)); }
    catch (e) {
      if (e?.response?.status === 401) setAuthed(false);
      else onToast?.(e?.response?.data?.detail || "Couldn't load leagues.");
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const dissolve = async (league) => {
    if (!window.confirm(`Dissolve '${league.name}'? This permanently deletes the league, its messages, applications, invites, tournaments, and matches. Teams will be unparented (not deleted). Type carefully — there's no undo.`)) return;
    setBusy(league.league_id);
    try {
      const res = await adminDissolveLeague(league.league_id);
      onToast?.(`💥 '${res.league_name}' dissolved. ${res.teams_unparented || 0} teams unparented.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't dissolve.");
    } finally { setBusy(""); }
  };

  const transfer = async (league) => {
    const target = window.prompt(`Transfer '${league.name}' to a player. Enter the new owner's user_id:`);
    if (!target) return;
    setBusy(league.league_id);
    try {
      const res = await adminTransferLeagueOwnership(league.league_id, target.trim());
      onToast?.(`👑 Ownership of '${league.name}' transferred to ${res.new_owner.name || target}.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't transfer.");
    } finally { setBusy(""); }
  };

  const pause = async (league) => {
    const willPause = !league.paused;
    const reason = willPause
      ? (window.prompt(`Pause '${league.name}'? Optional reason (visible in 409 errors):`, "") ?? null)
      : "";
    if (willPause && reason === null) return;  // user hit Cancel
    setBusy(league.league_id);
    try {
      const res = await adminPauseLeague(league.league_id, willPause, willPause ? reason : null);
      onToast?.(res.paused
        ? `⏸ '${league.name}' paused.`
        : `▶ '${league.name}' resumed.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't toggle pause.");
    } finally { setBusy(""); }
  };

  const exportJson = async (league) => {
    setBusy(league.league_id);
    try {
      const data = await exportLeague(league.league_id, "json");
      // Trigger a client-side download of the JSON.
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(league.slug || league.league_id)}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
      onToast?.(`📦 Exported '${league.name}' (${data?.counts?.members || 0} members).`);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't export.");
    } finally { setBusy(""); }
  };

  const exportCsv = async (league) => {
    setBusy(league.league_id);
    try {
      const blob = await exportLeague(league.league_id, "csv");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(league.slug || league.league_id)}_export.zip`;
      a.click();
      URL.revokeObjectURL(url);
      onToast?.(`📦 Exported '${league.name}' as CSV bundle.`);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't export.");
    } finally { setBusy(""); }
  };

  const clone = async (league) => {
    const name = window.prompt(
      `Clone '${league.name}' into a new league. Optional new name:`,
      `${league.name} · Season 2`,
    );
    if (name === null) return;
    setBusy(league.league_id);
    try {
      const res = await cloneLeague(league.league_id, { name: name.trim() || undefined });
      onToast?.(`🧬 Cloned → '${res.league.name}' (id ${res.league.league_id}).`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't clone.");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-leagues-section">
      <div className="flex gap-2 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && reload()}
          placeholder="Search by name / slug / game…"
          data-testid="admin-leagues-search"
          className="flex-1 bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
        />
        <button
          type="button"
          onClick={reload}
          data-testid="admin-leagues-search-btn"
          className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon"
        >
          SEARCH
        </button>
      </div>
      <div className="space-y-2" data-testid="admin-leagues-list">
        {rows.length === 0 ? (
          <p className="text-[12px] text-white/40 text-center py-10" data-testid="admin-leagues-empty">
            No leagues match.
          </p>
        ) : rows.map((league) => (
          <div
            key={league.league_id}
            data-testid={`admin-league-${league.league_id}`}
            className="glass rounded-2xl p-3 border border-white/10 flex flex-col sm:flex-row sm:items-center gap-2"
          >
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm flex items-center gap-2">
                <span className="truncate">{league.name}</span>
                <span className={`text-[9px] tracking-widest px-2 py-0.5 rounded-full ${
                  league.league_type === "PRO"
                    ? "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
                    : "bg-white/10 text-white/70 border border-white/15"
                }`}>{league.league_type}</span>
                {league.paused && (
                  <span
                    data-testid={`admin-league-paused-chip-${league.league_id}`}
                    className="text-[9px] tracking-widest px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/40"
                    title={league.paused_reason || ""}
                  >
                    <i className="fas fa-pause mr-1" />PAUSED
                  </span>
                )}
              </p>
              <p className="text-[10px] text-[#a3a3ac]">
                {league.game} · /{league.slug} · 👑 {league.owner_name} · {league.member_count} members
              </p>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={() => pause(league)}
                disabled={busy === league.league_id}
                data-testid={`admin-league-pause-${league.league_id}`}
                className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-yellow-500/10 text-yellow-300 border border-yellow-500/40 hover:bg-yellow-500/20 disabled:opacity-50"
              >
                <i className={`fas ${league.paused ? "fa-play" : "fa-pause"} mr-1`} />
                {league.paused ? "RESUME" : "PAUSE"}
              </button>
              <button
                type="button"
                onClick={() => clone(league)}
                disabled={busy === league.league_id}
                data-testid={`admin-league-clone-${league.league_id}`}
                className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50"
              >
                <i className="fas fa-clone mr-1" />CLONE
              </button>
              <button
                type="button"
                onClick={() => exportJson(league)}
                disabled={busy === league.league_id}
                data-testid={`admin-league-export-json-${league.league_id}`}
                className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-white/5 text-white/80 border border-white/15 hover:bg-white/10 disabled:opacity-50"
              >
                <i className="fas fa-file-code mr-1" />JSON
              </button>
              <button
                type="button"
                onClick={() => exportCsv(league)}
                disabled={busy === league.league_id}
                data-testid={`admin-league-export-csv-${league.league_id}`}
                className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-white/5 text-white/80 border border-white/15 hover:bg-white/10 disabled:opacity-50"
              >
                <i className="fas fa-file-csv mr-1" />CSV
              </button>
              <button
                type="button"
                onClick={() => transfer(league)}
                disabled={busy === league.league_id}
                data-testid={`admin-league-transfer-${league.league_id}`}
                className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50"
              >
                <i className="fas fa-crown mr-1" />TRANSFER
              </button>
              <button
                type="button"
                onClick={() => dissolve(league)}
                disabled={busy === league.league_id}
                data-testid={`admin-league-dissolve-${league.league_id}`}
                className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-red-500/10 text-red-300 border border-red-500/40 hover:bg-red-500/20 disabled:opacity-50"
              >
                <i className="fas fa-trash mr-1" />DISSOLVE
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
